import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Coins, Gift } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";

// Tip amounts in credits. Pulled from product spec, not the backend
// (MIN_TIP/MAX_TIP are the only enforced bounds).
const TIP_TIERS = [1, 5, 10, 25];

/**
 * TipDialog — one-shot credit transfer modal. Renders when `open=true`
 * and routes to `POST /api/tips`. Self-contained: caller only has to
 * pass `recipientUsername` + control the `open` boolean.
 *
 * Affordance ladder:
 *   - User can afford the selected tier → big primary Send button.
 *   - User cannot afford → CTA flips to "Top up X credits" routing to
 *     /pricing#topup with an explicit shortfall hint. NEVER silently
 *     fails — the user always knows exactly what to do next.
 */
const TipDialog = ({ open, onOpenChange, recipientUsername }) => {
  const { user, updateCredits } = useAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(5);
  const [submitting, setSubmitting] = useState(false);

  if (!recipientUsername) return null;

  const credits = user?.credits || 0;
  const canAfford = credits >= selected;
  const shortfall = Math.max(0, selected - credits);

  const send = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/tips", {
        recipient_username: recipientUsername,
        amount: selected,
      });
      toast.success(`Sent ${selected} credits to @${recipientUsername} 🎁`);
      if (typeof res.data?.new_balance === "number") {
        updateCredits(res.data.new_balance);
      }
      onOpenChange?.(false);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Couldn't send tip";
      if (status === 402) {
        // Server says insufficient — surface the top-up flow inline.
        toast.error(detail);
      } else {
        toast.error(detail);
      }
    }
    setSubmitting(false);
  };

  const goTopUp = () => {
    onOpenChange?.(false);
    // /pricing has the top-up package picker; hash anchors to the
    // top-up section so the user lands directly on the right tab.
    navigate(`/pricing#topup`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-sm" data-testid="tip-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-amber-300" />
            Tip <span className="text-emerald-300">@{recipientUsername}</span>
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Send credits straight to this creator — 100% goes to them, no fees.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-2 mt-2" data-testid="tip-amount-grid">
          {TIP_TIERS.map((amt) => (
            <button
              key={amt}
              type="button"
              onClick={() => setSelected(amt)}
              data-testid={`tip-amount-${amt}`}
              className={`p-3 rounded-lg border text-center font-bold transition-all ${
                selected === amt
                  ? "bg-amber-500/20 border-amber-500 text-amber-200"
                  : "bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600"
              }`}
            >
              <div className="text-base">{amt}</div>
              <div className="text-[10px] opacity-75 font-normal">cr</div>
            </button>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="text-gray-400 inline-flex items-center gap-1">
            <Coins className="h-3 w-3" /> Your balance: <span className={`tabular-nums font-mono ${canAfford ? "text-emerald-300" : "text-amber-300"}`}>{credits.toFixed(2)}</span>
          </span>
          {!canAfford && (
            <span className="text-amber-300 font-semibold" data-testid="tip-shortfall">
              Need {shortfall.toFixed(2)} more
            </span>
          )}
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={submitting}
            className="border-gray-700 text-gray-300 hover:bg-gray-800"
            data-testid="tip-cancel"
          >
            Cancel
          </Button>
          {canAfford ? (
            <Button
              type="button"
              onClick={send}
              disabled={submitting}
              data-testid="tip-send"
              className="bg-emerald-600 hover:bg-emerald-500 font-semibold"
            >
              {submitting ? "Sending…" : `Send ${selected} cr →`}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={goTopUp}
              data-testid="tip-topup"
              className="bg-amber-600 hover:bg-amber-500 font-semibold text-gray-900"
            >
              Top up {Math.ceil(shortfall)} cr →
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TipDialog;
